<template>
  <div
    v-if="user && user.id"
    class="notice-center"
  >
    <!-- Informativo obrigatorio: trava a tela ate marcar como lido; sem ESC e sem clicar fora -->
    <v-dialog
      :value="!!blockingNotice"
      persistent
      no-click-animation
      max-width="720px"
      content-class="notice-center__dialog"
      @keydown.esc.prevent
    >
      <v-card
        v-if="blockingNotice"
        data-test="informativo-obrigatorio"
      >
        <v-card-title class="notice-center__title">
          <PhWarningCircle
            :size="22"
            class="mr-2"
          />
          {{ blockingNotice.title }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div
            class="notice-center__content"
            v-html="blockingNotice.content"
          ></div>
          <p
            v-if="blockingRemaining > 0"
            class="notice-center__remaining"
          >{{ $tc('notices.center.remaining', blockingRemaining) }}</p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-end">
          <Button
            variant="primary"
            :loading="acknowledging === blockingNotice.id"
            data-test="botao-li-e-estou-ciente"
            @click="acknowledge(blockingNotice)"
          >
            {{ $t('notices.center.acknowledge') }}
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Informativos sem confirmacao: faixa no topo, fecham ao marcar como lido -->
    <div
      v-if="infoNotices.length"
      class="notice-center__banners"
      data-test="informativos-faixa"
    >
      <v-alert
        v-for="notice in infoNotices"
        :key="notice.id"
        type="info"
        text
        dense
        border="left"
        class="notice-center__banner"
        :data-test="`informativo-${notice.id}`"
      >
        <div class="notice-center__banner-body">
          <div>
            <strong>{{ notice.title }}</strong>
            <div
              class="notice-center__content notice-center__content--compact"
              v-html="notice.content"
            ></div>
          </div>
          <Button
            variant="tertiary"
            :loading="acknowledging === notice.id"
            :data-test="`marcar-lido-${notice.id}`"
            @click="acknowledge(notice)"
          >
            {{ $t('notices.center.markRead') }}
          </Button>
        </div>
      </v-alert>
    </div>
  </div>
</template>

<script>
// Components
import Button from '@/components/comum/Button.vue';
import { PhWarningCircle } from 'phosphor-vue';

// APIs
import NoticeApi from '@/api/notice/notice-api';

export default {
  name: 'NoticeCenter',

  components: { Button, PhWarningCircle },

  props: {
    user: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      notices: [],
      acknowledging: null,
      api: null
    };
  },

  computed: {
    blockingNotices() {
      return this.notices.filter((notice) => notice.requiresAck);
    },

    blockingNotice() {
      return this.blockingNotices[0] || null;
    },

    blockingRemaining() {
      return Math.max(0, this.blockingNotices.length - 1);
    },

    infoNotices() {
      return this.notices.filter((notice) => !notice.requiresAck);
    }
  },

  watch: {
    'user.id': {
      immediate: true,
      handler(id) {
        if (id) this.load();
        else this.notices = [];
      }
    },

    /* Trocar de tela confere de novo: um informativo publicado nesse meio tempo entra */
    '$route.path'() {
      if (this.user?.id) this.load();
    }
  },

  mounted() {
    this.$eventBus.$on('notices-updated', this.load);
  },

  beforeDestroy() {
    this.$eventBus.$off('notices-updated', this.load);
  },

  methods: {
    _api() {
      if (!this.api) this.api = new NoticeApi(this.user);

      return this.api;
    },

    async load() {
      try {
        const response = await this._api().findPending();

        this.notices = response.data || [];
      } catch {
        /* Sem resposta a tela segue; o proximo carregamento tenta de novo */
      }
    },

    async acknowledge(notice) {
      this.acknowledging = notice.id;

      try {
        await this._api().markRead(notice.id);
        this.notices = this.notices.filter((item) => item.id !== notice.id);
      } catch (error) {
        this.$root.$emit('sendMessage', this.$t('notices.center.ackError'), 'error');
      } finally {
        this.acknowledging = null;
      }
    }
  }
};
</script>

<style scoped>
.notice-center__title {
  gap: var(--tc-space-1);
  font-size: var(--tc-fs-h4);
  font-weight: var(--tc-fw-semibold);
}

.notice-center__content {
  font-size: var(--tc-fs-sm);
  line-height: 1.5;
  color: var(--tc-text);
}

.notice-center__content--compact {
  margin-top: var(--tc-space-1);
}

.notice-center__remaining {
  margin: var(--tc-space-3) 0 0;
  font-size: var(--tc-fs-label);
  color: var(--tc-text-2);
}

.notice-center__banners {
  padding: var(--tc-space-2) var(--tc-space-3) 0;
}

.notice-center__banner {
  margin-bottom: var(--tc-space-2);
}

.notice-center__banner-body {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--tc-space-3);
  flex-wrap: wrap;
}
</style>
